import { useState } from "react";
import { useEffect } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";

import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import { alpha, useTheme } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import UserDialog from "../../components/dialogs/userDialog";

import constants from "../../assets/constants";
import { createNewUser } from "../../data/omniDataModel";
import * as PracticeSessionActions from "../../state/redux/practiceSession";
import * as ProviderSessionActions from "../../state/redux/providerSession";
import { capitalizeAllWords, getAgeFromDateString, isValidUUID } from "../../utils/helpers";
import { deepCopyObject } from "../../utils/helpers";
import OmniAvatar from "../ui/omniAvatar";
import OmniEditButton from "../ui/omniEditButton";
import OmniResponsiveButton from "../ui/omniResponsiveButton.js";
import Widget from "../ui/widget";
import WidgetList from "../ui/widgetList";
import WidgetSkeleton from "../ui/widgetSkeleton";
import WidgetTitle from "../ui/widgetTitle";

const PracticeProviders = props => {
  const { relatedProviderODMs } = props;

  const [selectedProvider, setSelectedProvider] = useState(null);

  const getProviderInitials = providerODM => {
    let theReturn =
      providerODM.omniData.givenName.charAt(0) + providerODM.omniData.familyName.charAt(0);

    return theReturn.toUpperCase();
  };

  const getProviderDescription = providerODM => {
    let theReturn = "";
    if (providerODM.omniData.providerData.race != null) {
      theReturn += capitalizeAllWords(providerODM.omniData.providerData.race);
    }

    if (providerODM.omniData.providerData.ethnicity != null) {
      if (theReturn.length > 0) {
        theReturn += "  ";
      }
      theReturn += capitalizeAllWords(providerODM.omniData.providerData.ethnicity);
    }
    if (providerODM.omniData.providerData.gender != null) {
      if (theReturn.length > 0) {
        theReturn += "  ";
      }
      theReturn += capitalizeAllWords(providerODM.omniData.providerData.gender);
    }

    if (theReturn.length > 0) {
      theReturn += ",  ";
    }
    theReturn += `Age ${getAgeFromDateString(providerODM.omniData.providerData.birthdate)}`;

    return theReturn;
  };

  const loadProviderImage = providerId => {
    try {
      //TODO call DB here to get data retrieval
      let image = `${
        window.location.origin.includes("localhost")
          ? `https://dev.omnisciahealth.coach`
          : window.location.origin
      }/assets/demo/images/userImage(3).jpg`;

      if (providerId === constants.demoUserId.userTwo) {
        image = `${
          window.location.origin.includes("localhost")
            ? `https://dev.omnisciahealth.coach`
            : window.location.origin
        }/assets/demo/images/userImage(1).jpg`;
      }
      if (providerId === constants.demoUserId.userThree) {
        image = `${
          window.location.origin.includes("localhost")
            ? `https://dev.omnisciahealth.coach`
            : window.location.origin
        }/assets/demo/images/userImage(22).jpg`;
      }
      if (providerId === constants.demoUserId.userFour) {
        image = `${
          window.location.origin.includes("localhost")
            ? `https://dev.omnisciahealth.coach`
            : window.location.origin
        }/assets/demo/images/userImage(11).jpg`;
      }
      return image;
    } catch (error) {
      return `${
        window.location.origin.includes("localhost")
          ? `https://dev.omnisciahealth.coach`
          : window.location.origin
      }/assets/demo/images/userImage(3).jpg`;
    }
  };

  const navigateToManagePortal = () => {
    props.history.push(constants.appPaths.appPathAdminProvider);
  };

  const [titleText, setTitleText] = useState(null);

  useEffect(() => {
    if (relatedProviderODMs != null) {
      setTitleText(`${relatedProviderODMs.length} Providers`);
    } else setTitleText("");
  }, [relatedProviderODMs]);

  const theme = useTheme();

  return (
    <Widget>
      <WidgetTitle title={"Providers"} text={titleText} loading={relatedProviderODMs == null}>
        <OmniResponsiveButton
          tooltipTitle="Add a provider to this practice"
          buttonText="Provider"
          onClick={() => {
            setSelectedProvider(createNewUser(false, true));
          }}
        />
      </WidgetTitle>

      {relatedProviderODMs == null && <WidgetSkeleton />}
      {relatedProviderODMs != null && (
        <WidgetList>
          {relatedProviderODMs
            .sort((a, b) => a.omniData.familyName.localeCompare(b.omniData.familyName))
            .map((provider, index) => {
              return (
                <ListItem
                  key={`provider${index}`}
                  disableGutters={true}
                  sx={{
                    padding: theme.spacing(1),
                    paddingTop: 0,
                    paddingBottom: 0,
                    marginBottom: theme.spacing(1),
                    borderRadius: theme.spacing(1),
                    backgroundColor: alpha(theme.palette.info.main, 0.75),
                    color: theme.palette.info.contrastText,
                    cursor: "pointer",
                    boxShadow: theme.shadows[3],
                    userSelect: "none",
                    webkitUserSelect: "none",
                  }}
                  onClick={e => {
                    e.stopPropagation();
                    setSelectedProvider(provider);
                  }}
                >
                  {!isValidUUID(provider._id) && (
                    <Tooltip title={"No Notes"} placement="right">
                      <OmniAvatar
                        sx={{
                          width: 36,
                          height: 36,
                          fontSize: "1.8rem",
                          border: `2px solid ${theme.palette.info.contrastText}`,
                        }}
                        src={loadProviderImage(provider._id)}
                      ></OmniAvatar>
                    </Tooltip>
                  )}

                  {isValidUUID(provider._id) && (
                    <Tooltip title={"No Notes"} placement="right">
                      <OmniAvatar
                        sx={{
                          width: 36,
                          height: 36,
                          fontSize: "1.2rem",
                          color: theme.palette.info.main,
                          border: `2px solid ${theme.palette.info.contrastText}`,
                        }}
                      >
                        {getProviderInitials(provider)}
                      </OmniAvatar>
                    </Tooltip>
                  )}
                  <ListItemText
                    primary={
                      <Typography
                        variant="h6"
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          marginLeft: theme.spacing(1),
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          color: theme.palette.info.contrastText,
                          marginTop: 0,
                        }}
                      >
                        {provider.omniData.displayName
                          ? provider.omniData.displayName
                          : `${provider.omniData.givenName} ${provider.omniData.familyName}`}
                      </Typography>
                    }
                    secondary={
                      <Typography
                        variant="body2"
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          marginLeft: theme.spacing(1),
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          marginTop: "-6px",
                          color: theme.palette.info.contrastText,
                          minimumHeight: 20,
                        }}
                      >
                        {getProviderDescription(provider)}
                      </Typography>
                    }
                  />
                  <OmniEditButton
                    title={"Manage"}
                    onClick={e => {
                      e.stopPropagation();

                      props.loadProviderODMRequest(provider._id);
                      navigateToManagePortal();
                    }}
                    icon={
                      <OpenInNewIcon
                        sx={{
                          height: 42,
                          width: 42,
                        }}
                      />
                    }
                  />
                </ListItem>
              );
            })}
        </WidgetList>
      )}

      {selectedProvider && (
        <UserDialog
          open={selectedProvider != null}
          dialogTitle={`${selectedProvider._id == null ? "Add" : "Edit"} Provider`}
          omniODM={deepCopyObject(selectedProvider)}
          onDelete={provider => {
            props.deletePracticeProviderRequest(provider._id);
            setSelectedProvider(null);
          }}
          onAccept={provider => {
            props.updatePracticeProviderRequest(provider);
            setSelectedProvider(null);
          }}
          onClose={() => {
            setSelectedProvider(null);
          }}
        />
      )}
    </Widget>
  );
};
const mapStateToProps = state => ({
  relatedProviderODMs: state.practiceSession.relatedProviderODMs,
});

const mapDispatchToProps = {
  deletePracticeProviderRequest: PracticeSessionActions.deletePracticeProviderRequest,
  updatePracticeProviderRequest: PracticeSessionActions.updatePracticeProviderRequest,
  setProviderODMRequest: ProviderSessionActions.setProviderODMRequest,
  loadProviderODMRequest: ProviderSessionActions.loadProviderODMRequest,
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(PracticeProviders));
